import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Heart, PawPrint, Gift } from 'lucide-react';
import toast from 'react-hot-toast';
import { getCustomer } from '../api/customers';
import { listAppointments } from '../api/appointments';
import { listOrders } from '../api/marketplace';
import { listPayments } from '../api/payments';
import { listPets } from '../api/pets';
import { getCustomerLoyalty } from '../api/loyalty';
import { getErrorMessage } from '../api/response';
import Button from '../components/Button';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import { LoadingState, EmptyState } from '../components/PageState';
import PetDetailModal from '../components/PetDetailModal';
import { appointmentStatus, getStatusConfig, orderStatus, paymentStatus } from '../utils/status';
import { currency, date, dateTime } from '../utils/format';

const ClienteDetalle = () => {
  const { uuid } = useParams();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [pets, setPets] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [selectedPet, setSelectedPet] = useState(null);

  const loadDetail = useCallback(async () => {
    try {
      setLoading(true);
      const customerData = await getCustomer(uuid);

      const [petData, appointmentData, orderData, paymentData, loyaltyData] = await Promise.all([
        listPets({ customer_id: customerData.id, limit: 100 }),
        listAppointments({ customer_id: customerData.id, limit: 100 }),
        listOrders({ customer_id: customerData.id, limit: 100 }),
        listPayments({ customer_id: customerData.id, limit: 100 }),
        getCustomerLoyalty(customerData.id)
      ]);

      setCustomer(customerData);
      setPets(petData);
      setAppointments(appointmentData);
      setOrders(orderData);
      setPayments(paymentData);
      setLoyalty(loyaltyData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar la ficha del cliente'));
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const appointmentColumns = [
    {
      key: 'starts_at',
      label: 'Fecha',
      render: (row) => dateTime(row.starts_at)
    },
    {
      key: 'pet_id',
      label: 'Mascota',
      render: (row) => row.pet_name || `Mascota #${row.pet_id}`
    },
    {
      key: 'service_id',
      label: 'Servicio',
      render: (row) => row.service_name || `Servicio #${row.service_id}`
    },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => {
        const status = getStatusConfig(appointmentStatus, row.status);
        return <StatusBadge label={status.label} color={status.color} />;
      }
    }
  ];

  const orderColumns = [
    { key: 'order_number', label: 'Pedido' },
    { key: 'total_amount', label: 'Total', render: (row) => currency(row.total_amount) },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => {
        const status = getStatusConfig(orderStatus, row.status);
        return <StatusBadge label={status.label} color={status.color} />;
      }
    },
    { key: 'created_at', label: 'Fecha', render: (row) => date(row.created_at) }
  ];

  const paymentColumns = [
    { key: 'id', label: 'Pago', render: (row) => `#${row.id}` },
    { key: 'amount', label: 'Monto', render: (row) => currency(row.amount) },
    { key: 'method', label: 'Método', render: (row) => row.method || '-' },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => {
        const status = getStatusConfig(paymentStatus, row.status);
        return <StatusBadge label={status.label} color={status.color} />;
      }
    }
  ];

  if (loading) {
    return <LoadingState rows={8} />;
  }

  const pawsRemaining = loyalty?.current_paws ?? loyalty?.current_paw_count ?? 0;
  const pawsToReward = loyalty?.paws_required ?? 14;
  const hasFreeConsultation = Boolean(loyalty?.has_free_consultation);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-5 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-2xl font-bold text-gray-500">
            {customer?.photo_url ? (
              <img src={customer.photo_url} alt={customer.full_name} className="h-full w-full object-cover" />
            ) : (
              customer?.full_name?.slice(0, 1) || '#'
            )}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Ficha de cliente</p>
            <h1 className="text-2xl font-bold text-gray-950">{customer?.full_name || 'Cliente'}</h1>
            <p className="mt-1 text-sm text-gray-600">
              {[customer?.email, customer?.phone, customer?.address].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
            </p>
          </div>
        </div>
        <Link to="/clientes"><Button variant="secondary" icon={ArrowLeft}>Volver</Button></Link>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Mascotas" value={pets.length} icon={PawPrint} color="purple" />
        <StatCard title="Citas" value={appointments.length} icon={Calendar} color="blue" />
        <StatCard title="Ordenes" value={orders.length} icon={Gift} color="orange" />
        <StatCard title="Pagos" value={payments.length} icon={Heart} color="green" />
      </div>

      {/* Sección de Fidelización */}
      <section className="rounded-lg border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-transparent p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-950 flex items-center gap-2">
            <PawPrint className="h-5 w-5 text-blue-600" />
            Programa de fidelización
          </h2>
          {hasFreeConsultation && (
            <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center gap-1">
              <Gift className="h-3 w-3" /> Consulta gratis disponible
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-3">Patitas acumuladas</p>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex gap-1 flex-wrap">
                  {Array.from({ length: pawsToReward }).map((_, i) => (
                    <div
                      key={i}
                      className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        i < pawsRemaining
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {i + 1}
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">{pawsRemaining}</p>
                <p className="text-xs text-gray-500">de {pawsToReward}</p>
              </div>
            </div>
          </div>

          {loyalty?.rewards && loyalty.rewards.length > 0 && (
            <div>
              <p className="text-sm text-gray-600 mb-3">Recompensas disponibles</p>
              <div className="space-y-2">
                {loyalty.rewards.map((reward) => (
                  <div key={reward.id} className="rounded-lg bg-white p-3 border border-gray-200">
                    <p className="text-sm font-semibold text-gray-950">{reward.name}</p>
                    <p className="text-xs text-gray-500">
                      Vence: {date(reward.expires_at)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="mb-4 text-lg font-semibold text-gray-950">Mascotas asociadas</h2>
        {pets.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {pets.map((pet) => (
              <button
                key={pet.id}
                onClick={() => setSelectedPet(pet)}
                className="rounded-lg border border-gray-200 p-4 hover:border-blue-400 hover:bg-blue-50 transition text-left"
              >
                {pet.photo_url && (
                  <div className="mb-3 h-24 rounded-md bg-gray-100 overflow-hidden">
                    <img src={pet.photo_url} alt={pet.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <p className="font-semibold text-gray-900">{pet.name}</p>
                <p className="text-sm text-gray-600">
                  {pet.species || 'Especie desconocida'} · {pet.breed || pet.breed_name || 'Raza desconocida'}
                </p>
                <p className="mt-2 text-xs text-gray-500">
                  Nacido: {pet.birth_date ? date(pet.birth_date) : 'Desconocido'}
                </p>
              </button>
            ))}
          </div>
        ) : (
          <EmptyState
            title="Sin mascotas"
            message="Este cliente aún no tiene mascotas registradas"
          />
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="mb-4 text-lg font-semibold text-gray-950">Historial de citas</h2>
        {appointments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  {appointmentColumns.map(col => (
                    <th key={col.key} className="text-left py-3 px-4 font-semibold text-gray-700">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {appointments.map(row => (
                  <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                    {appointmentColumns.map(col => (
                      <td key={col.key} className="py-3 px-4">
                        {col.render ? col.render(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="Sin citas registradas" />
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="mb-4 text-lg font-semibold text-gray-950">Historial de compras</h2>
        {orders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  {orderColumns.map(col => (
                    <th key={col.key} className="text-left py-3 px-4 font-semibold text-gray-700">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map(row => (
                  <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                    {orderColumns.map(col => (
                      <td key={col.key} className="py-3 px-4">
                        {col.render ? col.render(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="Sin compras registradas" />
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="mb-4 text-lg font-semibold text-gray-950">Pagos</h2>
        {payments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  {paymentColumns.map(col => (
                    <th key={col.key} className="text-left py-3 px-4 font-semibold text-gray-700">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map(row => (
                  <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                    {paymentColumns.map(col => (
                      <td key={col.key} className="py-3 px-4">
                        {col.render ? col.render(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="Sin pagos registrados" />
        )}
      </section>

      <PetDetailModal
        pet={selectedPet}
        open={Boolean(selectedPet)}
        onClose={() => setSelectedPet(null)}
      />
    </div>
  );
};

export default ClienteDetalle;
